import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["abbreviate", "abbreviateThreshold", "value"];
import I18n from 'I18n';
import PropTypes from 'prop-types';
// @ts-expect-error untyped
import LegacyFormattedCurrency from '../../../components/FormattedCurrency';
// @ts-expect-error untyped
import { lookup } from '../translateHelpers';
import { INTL_DEFAULT_MIN_FRACTIONAL_DIGITS, getLocale, getOptions, alternativeFractionCurrencies } from './intlFormatters';
import { jsx as _jsx } from "react/jsx-runtime";
const NUMERAL_OR_SIGN_OR_E = /\d|\+|-|e/i;
/**
 * @deprecated Import from 'I18n/utils/formatIntlCurrency' instead
 */
const getDefaultCurrencyCodeForLocale = locale => {
  var _Object$values$find;
  if (!I18n.currencySymbols['USD']) {
    console.error('It looks like currency data is not available. Are you using the new I18n loader and forgot to import currencies? See go/new-i18n');
  }

  // The default currency code for a given locale is determined by the locale definition in i18n-data and retrieved via "lookup('number.currency.format.unit')"
  // https://git.hubteam.com/HubSpot/i18n-data/tree/master/data/locales
  const unit = lookup('number.currency.format.unit', {
    locale
  });
  if (!unit) {
    // Matching CURRENCY_FORMAT 's default currency symbol of '$'
    return 'USD';
  }
  return (_Object$values$find = Object.values(I18n.currencySymbols).find(currencyData => currencyData.symbol === unit)) === null || _Object$values$find === void 0 ? void 0 : _Object$values$find.code;
};
export function formatNumber(number, options) {
  return new Intl.NumberFormat(getLocale(options), getOptions(options)).format(number);
}

/**
 * @deprecated Import from 'I18n/utils/formatIntlCurrency' instead
 */
export function formatCurrency(number, options = {}) {
  var _options$currencyCode, _options$parts;
  const opts = Object.assign({
    style: 'currency',
    currency: (_options$currencyCode = options.currencyCode) !== null && _options$currencyCode !== void 0 ? _options$currencyCode : getDefaultCurrencyCodeForLocale(getLocale(options))
  }, getOptions(options));
  if (opts.currency && alternativeFractionCurrencies[opts.currency]) {
    opts.maximumFractionDigits = alternativeFractionCurrencies[opts.currency];
  }
  if (options.formatNegativeValuesWithParenthesis && number < 0) {
    // In many locales, accounting format means to wrap the number with parentheses instead of appending a minus sign (eg. English)
    // In other locales, a negative value is indicated by a minus sign (eg. German)
    opts.currencySign = 'accounting';
  }
  if (typeof options.unit === 'string' && options.unit) {
    options.parts = typeof options.parts === 'object' ? Object.assign({
      currency: options.unit
    }, options.parts) : {
      currency: options.unit
    };
  }
  if (!options.currencyCode && !((_options$parts = options.parts) !== null && _options$parts !== void 0 && _options$parts.currency)) {
    const locale = getLocale(options);
    const defaultCurrencyCode = getDefaultCurrencyCodeForLocale(locale);
    const message = 'I18n: Intl formatCurrency / FormattedCurrency called with missing currencyCode.';
    console.warn(`${message} Falling back to using default currency code: "${defaultCurrencyCode}" for the provided locale: "${locale}". Please provide a currencyCode to avoid this warning.`);
    if (window.Raven) {
      window.Raven.captureException(new Error(message), {
        extra: {
          defaultCurrencyCode,
          currentLocale: locale
        }
      });
    }
  }
  const formatter = new Intl.NumberFormat(getLocale(options), opts);
  if (options.parts) {
    return formatter.formatToParts(number).reduce((acc, {
      type,
      value
    }) => `${acc}${options.parts && typeof options.parts[type] !== 'undefined' ? options.parts[type] : value}`, '');
  }
  return formatter.format(number);
}
export function formatPercentage(number, options) {
  const opts = Object.assign({
    style: 'percent'
  }, getOptions(options));
  return new Intl.NumberFormat(getLocale(options), opts).format(number / 100);
}
export const parseNumber = function parseNumber(string, options) {
  var _options$parts2, _parts$find, _options$parts3, _parts$find2;
  /* Backwards compatiable version of I18n.parseNumber */
  // Use the getLocale utility
  const locale = getLocale(options);

  // Use getOptions to retrieve options like precision, currencyDisplay, etc.
  const opts = getOptions(options);

  // Handle fractional digits based on alternative currencies
  if (opts.currency && alternativeFractionCurrencies[opts.currency]) {
    opts.maximumFractionDigits = alternativeFractionCurrencies[opts.currency];
  } else if (typeof (options === null || options === void 0 ? void 0 : options.precision) !== 'number') {
    opts.maximumFractionDigits = INTL_DEFAULT_MIN_FRACTIONAL_DIGITS;
  }

  // If the input is already a number, return it
  if (typeof string === 'number') {
    return string;
  } else if (typeof string !== 'string') {
    return NaN;
  }

  // Remove all whitespace and replace MINUS SIGN with HYPHEN MINUS
  string = string.replace(/\s/g, '').replace('−', '-');

  // Parse number based on locale-specific separators
  const formatter = new Intl.NumberFormat(locale, opts);
  const parts = formatter.formatToParts(12345.6); // Use formatToParts for locale-specific parts
  const decimalSeparator = (options === null || options === void 0 || (_options$parts2 = options.parts) === null || _options$parts2 === void 0 ? void 0 : _options$parts2.decimal) || ((_parts$find = parts.find(part => part.type === 'decimal')) === null || _parts$find === void 0 ? void 0 : _parts$find.value);
  const groupSeparator = (options === null || options === void 0 || (_options$parts3 = options.parts) === null || _options$parts3 === void 0 ? void 0 : _options$parts3.group) || ((_parts$find2 = parts.find(part => part.type === 'group')) === null || _parts$find2 === void 0 ? void 0 : _parts$find2.value) || ',';

  // Iterate through characters and parse
  let buffer = '';
  for (const char of Array.from(string)) {
    if (char.match(NUMERAL_OR_SIGN_OR_E)) {
      buffer += char;
    } else if (char === decimalSeparator) {
      buffer += '.';
    } else if (char === groupSeparator) {
      continue; // Ignore group separators
    } else {
      return NaN;
    }
  }

  // Parse final string into a float
  return parseFloat(buffer);
};

/**
 * @deprecated Import from 'I18n/components/FormattedIntlCurrency' instead
 */

/**
 * @deprecated Import from 'I18n/components/FormattedIntlCurrency' instead
 */
export function FormattedCurrency(props) {
  try {
    const {
        abbreviate,
        abbreviateThreshold,
        value
      } = props,
      options = _objectWithoutPropertiesLoose(props, _excluded);
    const wrapperAttributes = {};
    const shouldAbbreviate = abbreviate && (typeof abbreviateThreshold !== 'number' || value >= abbreviateThreshold);
    if (shouldAbbreviate) {
      // Set the title attribute to the full, unabbreviated currency value
      wrapperAttributes.title = formatCurrency(value, options);
    }
    const formattedCurrency = formatCurrency(value, Object.assign({}, options, {
      abbreviate: shouldAbbreviate
    }));
    return /*#__PURE__*/_jsx("span", Object.assign({}, wrapperAttributes, {
      children: formattedCurrency
    }));
  } catch (e) {
    const message = `I18n: Intl FormattedCurrency Error - ${e}`;
    console.warn(message);
    if (window.Raven) {
      window.Raven.captureException(new Error(message), {
        extra: {
          currentLocale: getLocale(props)
        }
      });
    }
    return /*#__PURE__*/_jsx(LegacyFormattedCurrency, Object.assign({}, props));
  }
}
FormattedCurrency.propTypes = {
  abbreviate: PropTypes.bool,
  abbreviateThreshold: PropTypes.number,
  currencyCode: PropTypes.string,
  currencyDisplay: PropTypes.oneOf(['symbol', 'code', 'narrowSymbol', 'name']),
  formatNegativeValuesWithParenthesis: PropTypes.bool,
  currencySign: PropTypes.oneOf(['standard', 'accounting']),
  parts: PropTypes.shape({
    integer: PropTypes.string,
    group: PropTypes.string,
    decimal: PropTypes.string,
    fraction: PropTypes.string,
    literal: PropTypes.string,
    currency: PropTypes.string
  }),
  precision: PropTypes.number,
  maximumFractionDigits: PropTypes.number,
  simplifyCurrency: PropTypes.bool,
  stripInsignificantZeros: PropTypes.bool.isRequired,
  unit: PropTypes.string,
  useCurrencyCode: PropTypes.bool,
  value: PropTypes.number.isRequired
};
FormattedCurrency.defaultProps = {
  stripInsignificantZeros: false
};
export function FormattedPercentage(props) {
  return formatPercentage(props.value, props);
}